import React, { FunctionComponent, Suspense, useEffect } from 'react';

import { get } from 'lodash';
import { trackPromise } from 'react-promise-tracker';
import { BrowserRouter } from 'react-router-dom';

import { PreferenceKey, UITheme } from 'admin/users/Users';
import { CurrentContextProvider, ProfileProvider, useProfile } from 'auth/Auth';
import { Chrome } from 'common/chrome/Chrome';
import { Content, PublicContent } from 'common/content/Content';
import { ErrorBoundary } from 'common/error-boundary/ErrorBoundary';
import { JobsProvider } from 'common/jobs/Jobs';
import { MessageProvider } from 'common/message-bar/MessageBar';
import { Overlay } from 'core/overlay/Overlay';
import 'core/util/l10n';
import { ConfigProvider, useConfig } from 'core/util/Config';
import { HttpInterceptor } from 'core/util/Http';
import { isPublicRoute } from 'core/util/Routing';
import { TrackProvider } from 'core/util/Track';
import { promisify } from 'core/util/Util';
import { TicketReloadModalProvider } from 'tickets/actions2/common/components/TicketReloadModal';

import 'core/theme/Theme.scss';
import './global.scss';

const AppContainer: FunctionComponent = () => {
    const [profileContainer] = useProfile();
    const [configContainer] = useConfig();

    // Loading indicator
    useEffect(() => {
        trackPromise(promisify(!profileContainer.loading && !configContainer.loading), 'app-container');
    }, [profileContainer.loading, configContainer.loading]);

    if (profileContainer.loading || configContainer.loading) {
        return <Overlay area="app-container" />;
    }

    const theme = get(profileContainer.profile?.user.preferences, PreferenceKey.UITheme, UITheme.Light);

    document.body.classList.add(`r4e-theme-${theme}`);

    return (
        <TrackProvider>
            <MessageProvider>
                <JobsProvider>
                    <CurrentContextProvider>
                        <TicketReloadModalProvider>
                            <Chrome />
                            <HttpInterceptor>
                                <Content />
                            </HttpInterceptor>
                        </TicketReloadModalProvider>
                    </CurrentContextProvider>
                </JobsProvider>
            </MessageProvider>
        </TrackProvider>
    );
};

export const App: FunctionComponent = () => {
    if (isPublicRoute(window.location.pathname)) {
        return (
            <Suspense fallback={null}>
                <ErrorBoundary>
                    <BrowserRouter>
                        <PublicContent />
                    </BrowserRouter>
                </ErrorBoundary>
            </Suspense>
        );
    }

    return (
        <Suspense fallback={null}>
            <ErrorBoundary>
                <ProfileProvider>
                    <ConfigProvider>
                        <BrowserRouter>
                            <AppContainer />
                        </BrowserRouter>
                    </ConfigProvider>
                </ProfileProvider>
            </ErrorBoundary>
        </Suspense>
    );
};
